package com.eladfinish.bible.contest.screens.americanQuestions

import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.common.gender.getHebrewGender
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.currentMilliseconds
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.data.common.ClipboardCopier
import com.eladfinish.bible.contest.data.common.FileStorage
import com.eladfinish.bible.contest.di.config.DebugInfo
import com.eladfinish.bible.contest.extensions.convertToDataClass
import com.eladfinish.bible.contest.extensions.formatString
import com.eladfinish.bible.contest.extensions.padZero
import com.eladfinish.bible.contest.features.review.ReviewMoment
import com.eladfinish.bible.contest.features.review.ReviewPrompter
import com.eladfinish.bible.contest.network.error.utils.isInternetError
import com.eladfinish.bible.contest.repository.remoteConfig.RemoteConfig
import com.eladfinish.bible.contest.screens.americanQuestions.AmericanQuestionsState.DialogState
import com.eladfinish.bible.contest.screens.americanQuestions.model.AmericanQuestionItem
import com.eladfinish.bible.contest.screens.americanQuestions.model.AmericanQuestionSheetEntry
import com.eladfinish.bible.contest.screens.americanQuestions.model.QuestionResult
import com.eladfinish.bible.contest.screens.americanQuestions.usecases.GetAmericanQuestionsUseCase
import com.eladfinish.bible.contest.screens.americanQuestions.usecases.LoadPairsAsMultiChoiceQuestionsUseCase
import com.eladfinish.bible.contest.screens.mikud.MikudRepository
import com.eladfinish.bible.contest.screens.mikud.usecases.GetChaptersInMikudAsArrayListUseCase
import com.eladfinish.bible.contest.usecase.GetRemoteConfigValueUseCase
import com.russhwolf.settings.Settings
import io.github.aakira.napier.Napier
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.persistentListOf
import kotlinx.collections.immutable.toImmutableList
import kotlinx.collections.immutable.toPersistentList
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import kotlin.math.min
import kotlin.math.roundToInt
import kotlin.time.Duration.Companion.days
import kotlin.time.Duration.Companion.hours
import kotlin.time.Instant

class AmericanQuestionsStore(
    private val mikudRepository: MikudRepository,
    private val settings: Settings,
    private val assetReader: AssetReader,
    private val fileStorage: FileStorage,
    private val getRemoteConfigValueUseCase: GetRemoteConfigValueUseCase,
    private val getChaptersInMikudAsArrayListUseCase: GetChaptersInMikudAsArrayListUseCase,
    private val getAmericanQuestionsUseCase: GetAmericanQuestionsUseCase,
    private val loadPairsAsMultiChoiceQuestionsUseCase: LoadPairsAsMultiChoiceQuestionsUseCase,
    private val debugInfo: DebugInfo,
    private val clipboardCopier: ClipboardCopier,
    private val reviewPrompter: ReviewPrompter,
) : ViewModelStore<AmericanQuestionsState, AmericanQuestionsAction, AmericanQuestionsEffect>(
    tag = "AmericanQuestionsStore",
) {

    companion object {
        const val SKIP_READ_ME_PREF = "skipReadMeAmericanQuestions"
        private const val AMERICAN_QUESTIONS_LAST_DOWNLOADED_MILLIS = "americanQuestionsLastDownloadedMillis"
        private const val DAYS_NUMBER_RE_DOWNLOAD = 7

        // A full grade on a handful of questions says little about the game or the app.
        private const val MIN_QUESTIONS_FOR_REVIEW_MOMENT = 5
    }

    private val isSkipReadMeChecked: Boolean
        get() = settings.getBoolean(key = SKIP_READ_ME_PREF, defaultValue = false)

    private val numberOfQuestions: Int
        get() = getRemoteConfigValueUseCase.get<Int>(RemoteConfig.NUMBER_OF_QUESTIONS) ?: 10

    private val defaultState: AmericanQuestionsState =
        AmericanQuestionsState(
            isSkipReadMeChecked = isSkipReadMeChecked,
            isLoading = false,
            questions = emptyList<AmericanQuestionItem>().toImmutableList(),
            hebrewGender = settings.getHebrewGender(),
            dialogState = if (isSkipReadMeChecked) {
                DialogState.None
            } else {
                DialogState.Help(
                    isDontShowAgainChecked = isSkipReadMeChecked,
                    content = assetReader.readFile(
                        fileName = "other" + assetReader.fileSeparator + "americanGuide_part1.txt"
                    ),
                    isInitialHelp = true,
                )
            },
            enableCountdown = settings.getBoolean(
                key = Constants.PREF_ENABLE_COUNTDOWN_ON_AMERICAN_QUESTIONS,
                defaultValue = true,
            ),
            numberOfQuestions = numberOfQuestions,
        )

    override val stateFlow = MutableStateFlow(defaultState)

    val filterByMikud: Boolean
        get() = settings.getBoolean(key = Constants.PREF_MIKUD_FILTER_SWITCH, defaultValue = true)

    private val mikudFilenames: Set<String>
        get() = getChaptersInMikudAsArrayListUseCase(mikudStr = mikudRepository.mikudList).toSet()

    init {
        // If initial help dialog should be shown, defer question loading until it is dismissed.
        // Otherwise, start loading immediately.
        if (isSkipReadMeChecked) {
            dispatch(AmericanQuestionsAction.FetchQuestions)
        }
    }

    // ── Action dispatch ───────────────────────────────────────────────────────

    override fun handleAction(action: AmericanQuestionsAction): AmericanQuestionsState =
        when (action) {
            AmericanQuestionsAction.FetchQuestions -> handleFetchQuestions()
            AmericanQuestionsAction.FetchFromPairMatching -> handleFetchFromPairMatching()
            AmericanQuestionsAction.HelpClicked -> handleHelpClicked()
            AmericanQuestionsAction.HideDialog -> handleHideDialog()
            is AmericanQuestionsAction.ToggleHelp -> handleToggleHelp(action.shouldShowNextTime)
            AmericanQuestionsAction.SettingsClicked -> handleSettingsClicked()
            AmericanQuestionsAction.SettingsChanged -> handleSettingsChanged()
            is AmericanQuestionsAction.QuestionsReady -> handleQuestionsReady(action)
            is AmericanQuestionsAction.AnswerSelected -> handleAnswerSelected(action.answerIndex)
            AmericanQuestionsAction.AdvanceQuestion -> handleAdvanceQuestion()
            AmericanQuestionsAction.NewGame -> handleNewGame()
            is AmericanQuestionsAction.ShowHistory -> handleShowHistory(action.questionIndex)
            AmericanQuestionsAction.FiftyFiftyClicked -> handleFiftyFiftyClicked()
            is AmericanQuestionsAction.CopyQuestion -> handleCopyQuestion(action.text)
            AmericanQuestionsAction.RefreshClicked -> handleRefreshClicked()
        }

    // ── Question loading ──────────────────────────────────────────────────────

    private fun handleFetchFromPairMatching(): AmericanQuestionsState {
        // A rotation runs this again: the game in progress goes on.
        if (currentState.questions.isNotEmpty()) return currentState
        launchEffect {
            val items = loadPairsAsMultiChoiceQuestionsUseCase() ?: return@launchEffect
            dispatch(AmericanQuestionsAction.QuestionsReady(questions = items, fileContent = ""))
        }
        return currentState.copy(isLoading = true)
    }

    private fun handleFetchQuestions(): AmericanQuestionsState {

        val fileExists = fileStorage.fileExists(
            folder = Constants.AMERICAN_QUESTIONS_FOLDER,
            name = Constants.AMERICAN_QUESTIONS_FILENAME,
        )
        val lastDownloadedMillis = settings.getLong(
            key = AMERICAN_QUESTIONS_LAST_DOWNLOADED_MILLIS,
            defaultValue = 0L,
        )
        val threshold = if (debugInfo.isDebugMode) 7.hours.inWholeMilliseconds
        else DAYS_NUMBER_RE_DOWNLOAD.days.inWholeMilliseconds
        val isStillFresh = fileExists && (currentMilliseconds - lastDownloadedMillis) < threshold

        if (isStillFresh) {
            launchEffect {
                try {
                    val fileContent = fileStorage.readFile(
                        folder = Constants.AMERICAN_QUESTIONS_FOLDER,
                        name = Constants.AMERICAN_QUESTIONS_FILENAME,
                    ).orEmpty()
                    val entries = fileContent.convertToDataClass<List<AmericanQuestionSheetEntry>>()
                    val items = getAmericanQuestionsUseCase.processQuestions(
                        items = entries,
                        filterByMikud = filterByMikud,
                        mikudFilenames = mikudFilenames,
                    )
                    dispatch(AmericanQuestionsAction.QuestionsReady(questions = items, fileContent = fileContent))
                } catch (e: Exception) {
                    Napier.e("Error loading questions from file, downloading from server", e)
                    downloadFromServer()
                }
            }
        } else {
            launchEffect { downloadFromServer() }
        }

        return currentState.copy(isLoading = true)
    }

    private suspend fun downloadFromServer() {
        updateState { it.copy(dialogState = DialogState.DownloadingQuestions) }

        getAmericanQuestionsUseCase.fetch(
            filterByMikud = filterByMikud,
            mikudFilenames = mikudFilenames,
        ).fold(
            onSuccess = { (fileContent, items) ->
                fileStorage.saveFile(
                    folder = Constants.AMERICAN_QUESTIONS_FOLDER,
                    name = Constants.AMERICAN_QUESTIONS_FILENAME,
                    content = fileContent,
                )
                settings.putLong(
                    key = AMERICAN_QUESTIONS_LAST_DOWNLOADED_MILLIS,
                    value = currentMilliseconds,
                )
                dispatch(AmericanQuestionsAction.QuestionsReady(questions = items, fileContent = fileContent))
            },
            onFailure = { error ->
                if (error.isInternetError()) {
                    sideEffectFlow.emit(AmericanQuestionsEffect.NoInternet)
                }
                updateState { it.copy(isLoading = false, dialogState = DialogState.None) }
            },
        )
    }

    // ── Game actions ──────────────────────────────────────────────────────────

    private fun handleQuestionsReady(action: AmericanQuestionsAction.QuestionsReady): AmericanQuestionsState {
        val shuffled = action.questions.shuffled().toImmutableList()
        val state = currentState.copy(
            isLoading = false,
            dialogState = DialogState.None,
            questions = shuffled,
            numberOfQuestionsInFile = shuffled.size,
            remainingNumberOfQuestions = shuffled.size,
            currentQuestionIndex = -1,
            displayedQuestionIndex = -1,
            questionResults = persistentListOf(),
            questionDisplayStates = persistentListOf(),
            isGameOver = false,
            numberOfCorrectAnswers = 0,
            numberOfAskedQuestions = 0,
            usedFiftyFifty = false,
            disabledOptionIndices = persistentListOf(),
        )
        return state.advanceToNextQuestion()
    }

    private fun handleAnswerSelected(answerIndex: Int): AmericanQuestionsState {
        val currentIndex = currentState.currentQuestionIndex
        if (currentIndex < 0 || currentState.isGameOver || currentState.isCurrentQuestionAnswered) return currentState

        val isTimerExpired = answerIndex == -1
        val isCorrect = !isTimerExpired && answerIndex == currentState.correctAnswerIndex
        val result = if (isCorrect) QuestionResult.Correct else QuestionResult.Incorrect

        // Single emit avoids the DROP_OLDEST race that occurs when emitting
        // two consecutive effects (e.g. PlayWrongSound + Haptic) on the shared flow.
        launchEffect {
            sideEffectFlow.emit(
                AmericanQuestionsEffect.AnswerResult(
                    isCorrect = isCorrect,
                    isTimerExpired = isTimerExpired,
                )
            )
        }

        val updatedDisplayStates = currentState.questionDisplayStates.updatedDisplayState(currentIndex) {
            it.copy(userAnswerIndex = answerIndex)
        }

        return currentState.copy(
            questionResults = currentState.questionResults.updatedAt(
                index = currentIndex,
                value = result,
                padWith = QuestionResult.Unanswered,
            ),
            questionDisplayStates = updatedDisplayStates,
            numberOfCorrectAnswers = if (isCorrect) currentState.numberOfCorrectAnswers + 1 else currentState.numberOfCorrectAnswers,
            numberOfAskedQuestions = currentState.numberOfAskedQuestions + 1,
            remainingNumberOfQuestions = currentState.remainingNumberOfQuestions - 1,
        )
    }

    private fun handleAdvanceQuestion(): AmericanQuestionsState =
        currentState.advanceToNextQuestion()

    private fun AmericanQuestionsState.advanceToNextQuestion(): AmericanQuestionsState {
        val nextIndex = currentQuestionIndex + 1
        if (questions.isEmpty() || nextIndex >= effectiveNumberOfQuestions || remainingNumberOfQuestions == 0) {
            return announceEndOfGame()
        }

        val question = questions[nextIndex]
        val shuffled = question.shuffledOptions.toImmutableList()
        val correctIdx = shuffled.indexOfFirst { it == question.correctOption }.coerceAtLeast(0)
        val displayState = QuestionDisplayState(shuffledOptions = shuffled, correctOptionIndex = correctIdx)

        launchEffect { sideEffectFlow.emit(AmericanQuestionsEffect.SetFiftyFiftyEnabled(!usedFiftyFifty)) }

        return copy(
            currentQuestionIndex = nextIndex,
            displayedQuestionIndex = nextIndex,
            questionDisplayStates = questionDisplayStates.updatedAt(index = nextIndex, value = displayState, padWith = null),
            disabledOptionIndices = persistentListOf(),
        )
    }

    private fun AmericanQuestionsState.announceEndOfGame(): AmericanQuestionsState {
        launchEffect { sideEffectFlow.emit(AmericanQuestionsEffect.SetFiftyFiftyEnabled(false)) }
        if (numberOfAskedQuestions >= MIN_QUESTIONS_FOR_REVIEW_MOMENT) {
            val grade = (numberOfCorrectAnswers * 100.0 / numberOfAskedQuestions).roundToInt()
            launchEffect { reviewPrompter.onSuccessMoment(moment = ReviewMoment.QUIZ, grade = grade) }
        }
        return copy(isGameOver = true, currentQuestionIndex = -1, displayedQuestionIndex = -1)
    }

    private fun handleNewGame(): AmericanQuestionsState {
        val used = min(currentState.effectiveNumberOfQuestions, currentState.questions.size)
        val remaining = if (used < currentState.questions.size) {
            currentState.questions.subList(used, currentState.questions.size).shuffled()
        } else {
            currentState.questions.shuffled()
        }
        val newQuestions = remaining.toImmutableList()
        return currentState.copy(
            questions = newQuestions,
            numberOfQuestionsInFile = newQuestions.size,
            currentQuestionIndex = -1,
            displayedQuestionIndex = -1,
            questionResults = persistentListOf(),
            questionDisplayStates = persistentListOf(),
            isGameOver = false,
            numberOfCorrectAnswers = 0,
            numberOfAskedQuestions = 0,
            remainingNumberOfQuestions = newQuestions.size,
            usedFiftyFifty = false,
            disabledOptionIndices = persistentListOf(),
        ).advanceToNextQuestion()
    }

    private fun handleShowHistory(questionIndex: Int): AmericanQuestionsState {
        if (questionIndex >= currentState.questions.size) return currentState
        return currentState.copy(displayedQuestionIndex = questionIndex)
    }

    private fun handleFiftyFiftyClicked(): AmericanQuestionsState {
        if (currentState.isGameOver || currentState.usedFiftyFifty) return currentState
        val correctIndex = currentState.correctAnswerIndex
        val toDisable = (0..3).filter { it != correctIndex }.shuffled().dropLast(1).toImmutableList()
        launchEffect { sideEffectFlow.emit(AmericanQuestionsEffect.SetFiftyFiftyEnabled(false)) }
        return currentState.copy(usedFiftyFifty = true, disabledOptionIndices = toDisable)
    }

    private fun handleRefreshClicked(): AmericanQuestionsState {
        launchEffect { downloadFromServer() }
        return currentState.copy(
            currentQuestionIndex = -1,
            displayedQuestionIndex = -1,
            questionResults = persistentListOf(),
            questionDisplayStates = persistentListOf(),
            isGameOver = false,
            numberOfCorrectAnswers = 0,
            numberOfAskedQuestions = 0,
            usedFiftyFifty = false,
            disabledOptionIndices = persistentListOf(),
            isLoading = true,
        )
    }

    private fun handleSettingsChanged(): AmericanQuestionsState =
        currentState.copy(
            enableCountdown = settings.getBoolean(
                key = Constants.PREF_ENABLE_COUNTDOWN_ON_AMERICAN_QUESTIONS,
                defaultValue = true,
            ),
            hebrewGender = settings.getHebrewGender(),
        )

    private fun handleCopyQuestion(text: String): AmericanQuestionsState {
        clipboardCopier.copy(text)
        return onlyEffect(AmericanQuestionsEffect.CopiedToClipboard)
    }

    private fun handleSettingsClicked(): AmericanQuestionsState =
        onlyEffect(AmericanQuestionsEffect.OpenRandomVerseSettings)

    private fun handleHelpClicked(): AmericanQuestionsState {
        val lastDownloadedMillis = settings.getLong(
            key = AMERICAN_QUESTIONS_LAST_DOWNLOADED_MILLIS,
            defaultValue = 0L,
        )
        val lastDownloaded = if (lastDownloadedMillis > 0L) {
            formatEpochMillis(lastDownloadedMillis)
        } else {
            "N/A"
        }
        val content = buildString {
            append(assetReader.readFile(fileName = "other" + assetReader.fileSeparator + "americanGuide_part1.txt"))
            append(
                assetReader.readFile(fileName = "other" + assetReader.fileSeparator + "americanGuide_part2.txt")
                    .formatString(currentState.numberOfQuestionsInFile, lastDownloaded, DAYS_NUMBER_RE_DOWNLOAD)
            )
        }
        return currentState.copy(
            dialogState = DialogState.Help(
                isDontShowAgainChecked = currentState.isSkipReadMeChecked,
                content = content,
            ),
        )
    }

    private fun handleHideDialog(): AmericanQuestionsState {
        fetchQuestionsIfInitialHelpDismissed()
        return currentState.copy(dialogState = DialogState.None)
    }

    private fun handleToggleHelp(checked: Boolean): AmericanQuestionsState {
        settings.putBoolean(key = SKIP_READ_ME_PREF, value = checked)
        fetchQuestionsIfInitialHelpDismissed()
        return currentState.copy(isSkipReadMeChecked = checked)
    }

    /**
     * If the user is dismissing the initial help dialog (shown on first launch),
     * kick off question loading now — it was deferred until this moment.
     */
    private fun fetchQuestionsIfInitialHelpDismissed() {
        val isInitial = (currentState.dialogState as? DialogState.Help)?.isInitialHelp == true
        if (isInitial) {
            launchEffect { dispatch(AmericanQuestionsAction.FetchQuestions) }
        }
    }

    private fun formatEpochMillis(millis: Long): String {
        val local = Instant.fromEpochMilliseconds(millis)
            .toLocalDateTime(TimeZone.currentSystemDefault())
        return "${local.day.padZero()}." +
                "${local.month.number.padZero()}." +
                "${local.year} " +
                "${local.hour.padZero()}:" +
                local.minute.padZero()
    }

    // ── List utilities ────────────────────────────────────────────────────────

    private fun <T> ImmutableList<T>.updatedAt(index: Int, value: T, padWith: T): ImmutableList<T> {
        require(index >= 0)
        val mutable = toMutableList()
        while (mutable.size <= index) mutable.add(padWith)
        mutable[index] = value
        return mutable.toPersistentList()
    }

    private fun ImmutableList<QuestionDisplayState?>.updatedDisplayState(
        index: Int,
        transform: (QuestionDisplayState) -> QuestionDisplayState,
    ): ImmutableList<QuestionDisplayState?> {
        val existing = getOrNull(index) ?: return this
        return updatedAt(index = index, value = transform(existing), padWith = null)
    }

    private fun ImmutableList<QuestionResult>.updatedAt(
        index: Int,
        value: QuestionResult,
        padWith: QuestionResult,
    ): ImmutableList<QuestionResult> {
        require(index >= 0)
        val mutable = toMutableList()
        while (mutable.size <= index) mutable.add(padWith)
        mutable[index] = value
        return mutable.toPersistentList()
    }
}
